
/**
 * Shared contact name-search utility.
 *
 * Single source of truth for "search contacts by name" behaviour, reused by:
 *  - contact-list (main search list + entity/category character search)
 *  - go-app-customers-pane (base table)
 *  - search-suggestion-popover (top-N suggestions; the top N are guaranteed to be
 *    a prefix of the full list because both call this same ranking)
 *
 * Behaviour (agreed with product/QA):
 *  - Ranking tiers, best first:
 *      0 EXACT            full name equals the query
 *      1 STARTS_WITH      full name begins with the whole query
 *      2 TOKEN_PREFIX     every query word is the prefix of some (distinct) name word,
 *                         order-independent  -> "smith john" finds "John Michael Smith"
 *      3 CONTAINS         query appears mid-string (e.g. "van" in "Schvan") -> sinks to bottom
 *      4 FUZZY            typo-tolerant (Damerau-Levenshtein) -> light fallback only when nothing above matched
 *  - Within a tier results are ordered alphabetically (fuzzy tier by edit distance).
 *  - Fuzzy/typo matching only runs when tiers 0-3 produce zero results
 *    ("light typo tolerance, only when no exact/prefix/contains match").
 *  - Multi-word names (3-4 parts incl. middle name) are fully supported via tokenisation;
 *    word order does not matter (any-order cross matching).
 */

export const CONTACT_NAME_SEARCH_MIN_LENGTH = 2;

/**
 * Hybrid coverage threshold. When a multi-word query's all-token (AND) matches
 * number FEWER than this, partial (OR) matches — contacts matching only SOME of
 * the typed words — are appended BELOW the all-token matches so the user is never
 * stranded with too few results (and so result coverage is preserved). When the
 * all-token set is already this large or larger, partials are suppressed to keep
 * the list tight. Tunable per call via ContactNameSearchOptions.partialFallbackBelow.
 */
export const CONTACT_NAME_PARTIAL_FALLBACK_BELOW = 20;

/**
 * Pure-typo fallback cut-off: maximum normalised Damerau-Levenshtein distance
 * (edits / longest-token-length) between a typed word and a name word for the word
 * to be considered a typo match. Damerau counts an adjacent transposition as ONE
 * edit, so "schimdt" → "Schmidt" (a single i/m swap) scores 1/7 ≈ 0.14.
 *
 * 0.30 is deliberately just below 1/3: on a short ~6-letter word it admits ~1 edit
 * (keeping the real "Baumann" family for "bauman") but rejects the flood of 2-edit
 * look-alikes (Bayan, Baum, Bazan, Bäumer …) that 0.34 let through, while still
 * keeping genuine 2-edit typos on longer words (e.g. "baumna" → "Baumann" = 0.286).
 */
export const CONTACT_NAME_FUZZY_MAX_DISTANCE = 0.30;

export enum NameMatchTier {
  EXACT = 0,
  STARTS_WITH = 1,
  TOKEN_PREFIX = 2,
  CONTAINS = 3,
  /** Matched only SOME of the typed words (any-order). Always ranked below all-token matches. */
  PARTIAL = 4,
  FUZZY = 5,
}

export interface ContactNameSearchOptions {
  /** Cap the number of results returned (e.g. 5 for the popover). Unbounded when omitted. */
  limit?: number;
  /** Minimum query length before any search runs. Defaults to 2. */
  minQueryLength?: number;
  /** Enable the light typo-tolerant fuzzy fallback. Defaults to true. */
  fuzzyFallback?: boolean;
  /**
   * Append partial (OR) matches below the all-token matches when the all-token
   * set has fewer than this many results. Defaults to CONTACT_NAME_PARTIAL_FALLBACK_BELOW.
   * Set to 0 to disable partial fallback entirely (strict all-token / AND only).
   */
  partialFallbackBelow?: number;
  /**
   * Max normalised edit distance for the pure-typo fallback. Defaults to
   * CONTACT_NAME_FUZZY_MAX_DISTANCE. Lower = stricter (fewer typo matches).
   */
  fuzzyMaxDistance?: number;
}

export interface RankedNameResult<T> {
  item: T;
  tier: NameMatchTier;
  /** Lower is better. 0 for deterministic tiers, normalized edit distance for the fuzzy tier. */
  score: number;
  /** The name string that was matched against (normalised display name). */
  matchedName: string;
}

/** How to derive the searchable name from an item. */
export type NameSelector<T> = (item: T) => string;

const COMBINING_MARKS = /[̀-ͯ]/g;
const WHITESPACE = /\s+/;
// Anything that isn't a letter or number is treated as a word separator, so
// hyphens, apostrophes, dots and commas don't block matching:
// "Marie-Therese", "O'Brien", "A. K." all tokenise into clean words.
// \p{L}/\p{N} keep non-Latin scripts (e.g. CJK) intact.
const NON_WORD = /[^\p{L}\p{N}]+/gu;

/**
 * Lower-case, accent-stripped, punctuation-folded, trimmed. Safe on null/undefined.
 * German eszett (ß) is expanded to "ss" so "Straße" and "Strasse" match.
 */
export function normalizeName(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(NON_WORD, ' ')
    .trim();
}

/**
 * German-digraph expansion of umlauts: ä→ae, ö→oe, ü→ue, ß→ss.
 * Returns '' when the value has no umlaut (so callers can skip a redundant form).
 * Used as an ADDITIONAL searchable form per name so that a user typing "mueller"
 * (no umlaut keyboard) matches "Müller" without the false-merge risk of collapsing
 * every "ue" (which would wrongly equate e.g. "Bauer" and "Baur").
 */
export function expandedGermanName(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const raw = value.toString().toLowerCase();
  if (!/[äöüß]/.test(raw)) {
    return '';
  }
  const expanded = raw
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(NON_WORD, ' ')
    .trim();
  return expanded;
}

/** Split a value into normalised word tokens. */
export function tokenizeName(value: string | null | undefined): string[] {
  const normalized = normalizeName(value);
  return normalized ? normalized.split(WHITESPACE).filter(Boolean) : [];
}

/** A single searchable representation of a name: its normalised string and word tokens. */
interface NameForm {
  readonly norm: string;
  readonly tokens: string[];
}

/**
 * Memo of name -> searchable forms. Normalisation (NFD + several regex passes) is the
 * hot cost when ranking thousands of contacts on every keystroke; since forms are a pure
 * function of the raw name string, caching them makes repeated searches O(n) comparisons
 * with no re-normalisation. Bounded so a long-lived session can't grow it without limit.
 */
const NAME_FORMS_CACHE_LIMIT = 50_000;
const nameFormsCache = new Map<string, readonly NameForm[]>();

/**
 * All searchable forms for a name: the accent-stripped base form, plus the
 * German-expanded form when the name contains umlauts. Each form carries its
 * own token list. De-duplicated and memoised.
 */
function buildNameForms(name: string): readonly NameForm[] {
  const cached = nameFormsCache.get(name);
  if (cached) {
    return cached;
  }
  const forms: NameForm[] = [];
  const base = normalizeName(name);
  if (base) {
    forms.push({ norm: base, tokens: base.split(WHITESPACE).filter(Boolean) });
  }
  const expanded = expandedGermanName(name);
  if (expanded && expanded !== base) {
    forms.push({ norm: expanded, tokens: expanded.split(WHITESPACE).filter(Boolean) });
  }
  if (nameFormsCache.size >= NAME_FORMS_CACHE_LIMIT) {
    nameFormsCache.clear();
  }
  nameFormsCache.set(name, forms);
  return forms;
}

/**
 * Default name selector for Exeevo Contact-shaped objects.
 * Combines firstName + middleName + lastName so 3-4 part names are searchable,
 * falling back to the stored fullname when individual parts are missing.
 */
export function defaultContactName(item: any): string {
  if (!item) {
    return '';
  }
  const parts = [item.firstName, item.middleName, item.lastName]
    .map((p: any) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  if (parts.length) {
    return parts.join(' ');
  }
  return item.fullname || item.name || '';
}

/**
 * Returns true when every query token is a prefix of a distinct name token.
 * Order-independent (greedy distinct matching) so "smith john" matches "John Smith".
 */
function tokensPrefixMatch(nameTokens: string[], queryTokens: string[]): boolean {
  if (!queryTokens.length || queryTokens.length > nameTokens.length) {
    return false;
  }
  const used = new Array(nameTokens.length).fill(false);
  for (const q of queryTokens) {
    let matched = false;
    for (let i = 0; i < nameTokens.length; i++) {
      if (!used[i] && nameTokens[i].startsWith(q)) {
        used[i] = true;
        matched = true;
        break;
      }
    }
    if (!matched) {
      return false;
    }
  }
  return true;
}

/** Returns true when every query token appears somewhere inside a distinct name token. */
function tokensContainMatch(nameTokens: string[], queryTokens: string[]): boolean {
  if (!queryTokens.length) {
    return false;
  }
  const used = new Array(nameTokens.length).fill(false);
  for (const q of queryTokens) {
    let matched = false;
    for (let i = 0; i < nameTokens.length; i++) {
      if (!used[i] && nameTokens[i].includes(q)) {
        used[i] = true;
        matched = true;
        break;
      }
    }
    if (!matched) {
      return false;
    }
  }
  return true;
}

/** Classify a single name against the query into a deterministic tier (0-3) or null. */
function classify(
  nameNorm: string,
  nameTokens: string[],
  queryNorm: string,
  queryTokens: string[],
): NameMatchTier | null {
  if (!nameNorm) {
    return null;
  }
  if (nameNorm === queryNorm) {
    return NameMatchTier.EXACT;
  }
  if (nameNorm.startsWith(queryNorm)) {
    return NameMatchTier.STARTS_WITH;
  }
  if (tokensPrefixMatch(nameTokens, queryTokens)) {
    return NameMatchTier.TOKEN_PREFIX;
  }
  if (nameNorm.includes(queryNorm) || tokensContainMatch(nameTokens, queryTokens)) {
    return NameMatchTier.CONTAINS;
  }
  return null;
}

/**
 * For a single query token, the best tier it achieves against a name's tokens:
 * TOKEN_PREFIX (2) if a name word starts with it, CONTAINS (3) if it appears
 * inside a name word, otherwise null (no match).
 */
function singleTokenTier(nameTokens: string[], q: string): NameMatchTier | null {
  let best: NameMatchTier | null = null;
  for (const nt of nameTokens) {
    if (nt.startsWith(q)) {
      return NameMatchTier.TOKEN_PREFIX; // best achievable for one token
    }
    if (nt.includes(q)) {
      best = NameMatchTier.CONTAINS;
    }
  }
  return best;
}

/**
 * Partial (OR) match info across all forms of a name: how many of the query's
 * words matched (any-order), and the best tier among them. Used to rank partial
 * matches below all-token matches (more words matched ranks higher).
 */
function partialMatch(
  forms: readonly NameForm[],
  queryTokens: string[],
): { matched: number; tier: NameMatchTier } {
  let matched = 0;
  let bestTier: NameMatchTier = NameMatchTier.CONTAINS;
  for (const q of queryTokens) {
    let tierForToken: NameMatchTier | null = null;
    for (const form of forms) {
      const t = singleTokenTier(form.tokens, q);
      if (t !== null && (tierForToken === null || t < tierForToken)) {
        tierForToken = t;
      }
    }
    if (tierForToken !== null) {
      matched++;
      if (tierForToken < bestTier) {
        bestTier = tierForToken;
      }
    }
  }
  return { matched, tier: bestTier };
}

/**
 * Optimal String Alignment (Damerau-Levenshtein) distance — like Levenshtein but
 * an adjacent transposition counts as a single edit. Bounded early via `max`:
 * returns max+1 as soon as the best possible distance exceeds it.
 */
function damerauLevenshtein(a: string, b: string, max: number): number {
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) {
    return max + 1;
  }
  if (!al) return bl;
  if (!bl) return al;
  let prevPrev = new Array(bl + 1).fill(0);
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, prevPrev[j - 2] + 1);
      }
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) {
      return max + 1;
    }
    const tmp = prevPrev;
    prevPrev = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[bl];
}

/**
 * Smallest normalised typo distance (edits / longest length) between a query word
 * and any word of the name across its forms. 0 = identical, 1 = no resemblance.
 */
function bestTypoDistance(
  forms: readonly NameForm[],
  q: string,
  maxNorm: number,
): number {
  let best = 1;
  for (const form of forms) {
    for (const t of form.tokens) {
      const longest = Math.max(q.length, t.length);
      if (!longest) continue;
      // DL >= |length difference|; skip tokens that can't beat the current best.
      const cap = Math.ceil(Math.min(maxNorm, best) * longest);
      if (Math.abs(q.length - t.length) > cap) continue;
      const d = damerauLevenshtein(q, t, cap);
      const nd = d / longest;
      if (nd < best) best = nd;
      if (best === 0) return 0;
    }
  }
  return best;
}

/**
 * How many query words EXACTLY equal a distinct name word (any order, across forms).
 * Used to rank exact whole-word matches above prefix-of-longer-word matches within a
 * tier — e.g. for "berg", "Birthe Berg" (word "berg") ranks above "Annette Bergmann"
 * (where "berg" is only the start of "bergmann").
 */
function exactWordCount(forms: readonly NameForm[], queryTokens: string[]): number {
  let best = 0;
  for (const form of forms) {
    const used = new Array(form.tokens.length).fill(false);
    let count = 0;
    for (const q of queryTokens) {
      for (let i = 0; i < form.tokens.length; i++) {
        if (!used[i] && form.tokens[i] === q) {
          used[i] = true;
          count++;
          break;
        }
      }
    }
    if (count > best) best = count;
  }
  return best;
}

/**
 * How many query words are at least APPROXIMATELY present in the name — matched by
 * prefix/contains, or a close typo (within `maxNorm`). Used to rank partial matches:
 * a name that covers more of the query (e.g. "Leyla Abdullah" for "layla abdu",
 * where "layla"≈"leyla") ranks above one that shares only a single word.
 */
function coverageCount(
  forms: readonly NameForm[],
  queryTokens: string[],
  maxNorm: number,
): number {
  let count = 0;
  for (const q of queryTokens) {
    let covered = false;
    for (const form of forms) {
      if (form.tokens.some((w) => w.startsWith(q) || w.includes(q))) {
        covered = true;
        break;
      }
    }
    if (!covered && bestTypoDistance(forms, q, maxNorm) <= maxNorm) {
      covered = true;
    }
    if (covered) count++;
  }
  return count;
}

/**
 * Rank items by name relevance. Returns full result objects (tier + score) for
 * callers that need ordering metadata or highlighting.
 */
export function rankContactsByName<T>(
  items: T[],
  query: string,
  options: ContactNameSearchOptions = {},
  nameSelector: NameSelector<T> = defaultContactName,
): RankedNameResult<T>[] {
  const minLength = options.minQueryLength ?? CONTACT_NAME_SEARCH_MIN_LENGTH;
  const fuzzyFallback = options.fuzzyFallback ?? true;
  const queryNorm = normalizeName(query);

  if (!items || !items.length || queryNorm.length < minLength) {
    return [];
  }
  const queryTokens = tokenizeName(query);

  // Pre-compute searchable forms (base + German-expanded) and tokens once.
  const prepared = items
    .map((item) => {
      const name = nameSelector(item);
      const forms = buildNameForms(name);
      return { item, name, forms, nameNorm: forms.length ? forms[0].norm : '' };
    })
    .filter((p) => p.forms.length);

  const collate = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

  const maxNorm = options.fuzzyMaxDistance ?? CONTACT_NAME_FUZZY_MAX_DISTANCE;

  // --- All-token (AND) matches — every typed word matches the name. ---
  const allToken: Array<RankedNameResult<T> & { exactWords: number }> = [];
  // --- Partial (OR) candidates — only SOME typed words match (multi-word queries). ---
  // Kept lightweight here; the expensive coverage/exact-word keys are computed lazily
  // below, only if these partials actually get used (see the hybrid gate).
  const partialCandidates: Array<{
    item: T;
    forms: readonly NameForm[];
    matchedName: string;
    matched: number;
    matchTier: NameMatchTier;
  }> = [];

  for (const p of prepared) {
    let bestTier: NameMatchTier | null = null;
    for (const form of p.forms) {
      const tier = classify(form.norm, form.tokens, queryNorm, queryTokens);
      if (tier !== null && (bestTier === null || tier < bestTier)) {
        bestTier = tier;
      }
    }
    if (bestTier !== null) {
      allToken.push({
        item: p.item, tier: bestTier, score: 0, matchedName: p.nameNorm,
        exactWords: exactWordCount(p.forms, queryTokens),
      });
      continue;
    }
    // Partial matching only applies to multi-word queries (a 1-word query that
    // doesn't match all-token can't match "some" either).
    if (queryTokens.length >= 2) {
      const pm = partialMatch(p.forms, queryTokens);
      if (pm.matched >= 1) {
        partialCandidates.push({
          item: p.item, forms: p.forms, matchedName: p.nameNorm,
          matched: pm.matched, matchTier: pm.tier,
        });
      }
    }
  }

  // All-token: by tier, then exact-word matches above prefix-of-longer-word, then A-Z.
  // (e.g. "berg" -> "Birthe Berg" ranks above "Annette Bergmann".)
  allToken.sort((a, b) =>
    a.tier !== b.tier
      ? a.tier - b.tier
      : b.exactWords !== a.exactWords
        ? b.exactWords - a.exactWords
        : collate(a.matchedName, b.matchedName),
  );

  // Hybrid coverage: keep the list tight when there are already enough all-token
  // matches; otherwise append the partial (OR) matches below so coverage is preserved.
  const fallbackBelow = options.partialFallbackBelow ?? CONTACT_NAME_PARTIAL_FALLBACK_BELOW;
  let results: RankedNameResult<T>[];
  if (allToken.length >= fallbackBelow || !partialCandidates.length) {
    results = allToken;
  } else {
    // Partial (OR) ordering — fully deterministic (no whole-string fuzzy score, which was
    // length-biased): most words matched, then how much of the query is approximately
    // present (coverage keeps "layla abdu" -> "Leyla Abdullah" on top), then exact-word
    // over prefix, then match quality, then alphabetically. This makes the tail predictable
    // (e.g. for "benjamin brun" the "…Brun*" names are simply alphabetical, not length-ranked).
    // Coverage (which runs edit-distance) is computed here, only now that we know these
    // partials will be used.
    const partial = partialCandidates.map((c) => ({
      item: c.item,
      tier: NameMatchTier.PARTIAL,
      score: 0,
      matchedName: c.matchedName,
      matched: c.matched,
      matchTier: c.matchTier,
      coverage: coverageCount(c.forms, queryTokens, maxNorm),
      exactWords: exactWordCount(c.forms, queryTokens),
    }));
    partial.sort((a, b) =>
      b.matched !== a.matched
        ? b.matched - a.matched
        : b.coverage !== a.coverage
          ? b.coverage - a.coverage
          : b.exactWords !== a.exactWords
            ? b.exactWords - a.exactWords
            : a.matchTier !== b.matchTier
              ? a.matchTier - b.matchTier
              : collate(a.matchedName, b.matchedName),
    );
    results = (allToken as RankedNameResult<T>[]).concat(partial);
  }

  // Typo-similar augmentation. Append close typo matches BELOW the strong matches so
  // misspellings surface similar contacts, and so a mistype behaves the same whether or
  // not it happens to be a clean prefix (e.g. "bauman" -> the Baumanns + similar).
  // Single-word queries ALWAYS get this pass: they have no partial (OR) path and benefit
  // most from typo tolerance, and their strong-match count can be inflated by mid-word
  // "contains" hits (e.g. "hubert" inside "Schubert") that would otherwise suppress it.
  // Multi-word queries only augment when the strong-match set is sparse.
  // Ranked by per-word Damerau-Levenshtein distance (a transposition = 1 edit), so the
  // genuinely-closest word wins regardless of where it sits in the name — this is also
  // why "schimdt" surfaces "Schmidt", not the position-biased look-alike "Schide".
  const augmentWithFuzzy =
    fuzzyFallback && (queryTokens.length === 1 || results.length < fallbackBelow);
  if (augmentWithFuzzy) {
    const maxNorm = options.fuzzyMaxDistance ?? CONTACT_NAME_FUZZY_MAX_DISTANCE;
    const fuzzyTokens = queryTokens.filter((t) => t.length >= minLength);
    if (fuzzyTokens.length) {
      const already = new Set(results.map((r) => r.item));
      const scored: RankedNameResult<T>[] = [];
      for (const p of prepared) {
        if (already.has(p.item)) continue; // keep the strong matches on top, don't duplicate
        // Every typed word must be a close typo of some word in the name.
        let total = 0;
        let ok = true;
        for (const q of fuzzyTokens) {
          const d = bestTypoDistance(p.forms, q, maxNorm);
          if (d > maxNorm) {
            ok = false;
            break;
          }
          total += d;
        }
        if (ok) {
          scored.push({
            item: p.item,
            tier: NameMatchTier.FUZZY,
            score: total / fuzzyTokens.length,
            matchedName: p.nameNorm,
          });
        }
      }
      scored.sort((a, b) =>
        a.score !== b.score ? a.score - b.score : collate(a.matchedName, b.matchedName),
      );
      results = results.concat(scored);
    }
  }

  return typeof options.limit === 'number' ? results.slice(0, options.limit) : results;
}

/**
 * Convenience wrapper that returns the ranked original items only.
 * Use this for the list / popover where the ordering metadata is not needed.
 */
export function searchContactsByName<T>(
  items: T[],
  query: string,
  options: ContactNameSearchOptions = {},
  nameSelector: NameSelector<T> = defaultContactName,
): T[] {
  return rankContactsByName(items, query, options, nameSelector).map((r) => r.item);
}

/**
 * Wrap the portions of `text` that match the query with <b> tags, for result highlighting.
 * Matches whole-query and per-token prefixes/substrings (case/accent-insensitive),
 * while preserving the original casing of `text`.
 */
export function highlightNameMatch(text: string, query: string): string {
  if (!text) {
    return '';
  }
  const queryTokens = tokenizeName(query);
  if (!queryTokens.length) {
    return text;
  }
  // Build a normalised view of the original text so we can map match indices back.
  const normChars: string[] = [];
  const map: number[] = []; // normChars index -> original index
  for (let i = 0; i < text.length; i++) {
    const n = text[i].normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
    for (const ch of n) {
      normChars.push(ch);
      map.push(i);
    }
  }
  const normStr = normChars.join('');

  const ranges: Array<[number, number]> = [];
  for (const token of queryTokens) {
    let from = 0;
    let idx = normStr.indexOf(token, from);
    while (idx !== -1) {
      ranges.push([map[idx], map[idx + token.length - 1]]);
      from = idx + token.length;
      idx = normStr.indexOf(token, from);
    }
  }
  if (!ranges.length) {
    return text;
  }
  // Merge overlapping ranges.
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of ranges) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1] + 1) {
      last[1] = Math.max(last[1], e);
    } else {
      merged.push([s, e]);
    }
  }

  let out = '';
  let cursor = 0;
  for (const [s, e] of merged) {
    out += text.slice(cursor, s);
    out += `<b>${text.slice(s, e + 1)}</b>`;
    cursor = e + 1;
  }
  out += text.slice(cursor);
  return out;
}

/** Per-word breakdown of how a query word matched a name word. */
export interface NameWordMatch {
  queryWord: string;
  /** The name word it was compared against (closest one). */
  nameWord: string;
  /** Damerau-Levenshtein edit count (0 = identical; a letter swap = 1). */
  edits: number;
  /** edits / longest-word-length (what the fuzzy cap is compared against). */
  normalizedDistance: number;
  /** How this word matched: 'exact' | 'prefix' | 'contains' | 'fuzzy' | 'none'. */
  how: 'exact' | 'prefix' | 'contains' | 'fuzzy' | 'none';
}

/** Diagnostic explanation of why (and how well) a query matches a name. */
export interface NameMatchExplanation {
  query: string;
  name: string;
  normalizedQuery: string;
  normalizedName: string;
  /** Overall tier the name lands in for this query (null = no match at all). */
  tier: NameMatchTier | null;
  tierName: string;
  /** Per typed-word breakdown (edits + how each matched). */
  words: NameWordMatch[];
}

/**
 * Developer/QA diagnostic: explains how a single query matches a single name —
 * the overall tier plus, for each typed word, the closest name word, the number of
 * edits, and the normalized distance the fuzzy cap is measured against.
 *
 * Example:
 *   explainNameMatch('jon', 'John Adams')
 *   // tier: FUZZY, words: [{ queryWord:'jon', nameWord:'john', edits:1, normalizedDistance:0.25, how:'fuzzy' }]
 */
export function explainNameMatch(
  query: string,
  name: string,
  options: Pick<ContactNameSearchOptions, 'fuzzyMaxDistance'> = {},
): NameMatchExplanation {
  const maxNorm = options.fuzzyMaxDistance ?? CONTACT_NAME_FUZZY_MAX_DISTANCE;
  const queryNorm = normalizeName(query);
  const queryTokens = tokenizeName(query);
  const forms = buildNameForms(name);
  const nameNorm = forms.length ? forms[0].norm : '';

  // Overall tier (mirrors rankContactsByName's classification).
  let tier: NameMatchTier | null = null;
  for (const form of forms) {
    const t = classify(form.norm, form.tokens, queryNorm, queryTokens);
    if (t !== null && (tier === null || t < tier)) tier = t;
  }
  if (tier === null && queryTokens.length >= 2 && partialMatch(forms, queryTokens).matched >= 1) {
    tier = NameMatchTier.PARTIAL;
  }
  if (tier === null && queryTokens.length) {
    const everyWordClose = queryTokens.every((q) => bestTypoDistance(forms, q, maxNorm) <= maxNorm);
    if (everyWordClose) tier = NameMatchTier.FUZZY;
  }

  const allNameTokens = Array.from(new Set(forms.flatMap((f) => f.tokens)));
  const words: NameWordMatch[] = queryTokens.map((q) => {
    let best = { nameWord: '', edits: Infinity, norm: 1 };
    for (const w of allNameTokens) {
      const longest = Math.max(q.length, w.length) || 1;
      const e = damerauLevenshtein(q, w, longest);
      if (e < best.edits) best = { nameWord: w, edits: e, norm: e / longest };
    }
    let how: NameWordMatch['how'] = 'none';
    if (best.nameWord === q) how = 'exact';
    else if (allNameTokens.some((w) => w.startsWith(q))) how = 'prefix';
    else if (allNameTokens.some((w) => w.includes(q))) how = 'contains';
    else if (best.norm <= maxNorm) how = 'fuzzy';
    return {
      queryWord: q, nameWord: best.nameWord, edits: best.edits,
      normalizedDistance: Number(best.norm.toFixed(3)), how,
    };
  });

  return {
    query, name, normalizedQuery: queryNorm, normalizedName: nameNorm,
    tier, tierName: tier === null ? 'NO MATCH' : NameMatchTier[tier], words,
  };
}
